import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { tagsPropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { Card, CardTitle } from 'components/Card';
import Message from 'components/Message';
import Prompt from 'components/Prompt';
import SaveBar from 'components/SaveBar';
import { Title } from 'features/App';
import { ErrorMessage, Form, PhoneNumberInput, Switch } from 'components/HookForm';
import LoadingDots from 'components/LoadingDots';
import appMessages from 'features/App/App.messages';
import { useIsMarketingOptInEnabled } from 'hooks/useIsMarketingOptInEnabled';
import MarketingEmails from './MarketingEmails';
import messages, { notificationTypeMessages } from './Notifications.messages';
import OrderNotificationsSection from './OrderNotificationsSection';
import { NOTIFICATION_TYPE, NOTIFICATION_TYPE_LIST } from './Notifications.constants';
import VenueDropdown, { ALL_VENUES_VALUE } from './VenueDropdown';
import VenueEmailsSection from './VenueEmailsSection';
import './Notifications.scss';

const FORM_NAME = 'NOTIFICATIONS_FORM';
const { element } = bem('notifications');

// The field only accepts US numbers and is disabled while SMS is off, so a half-typed or
// legacy non-US number would otherwise fail `tel` validation with no way for the user to
// correct it (ST-8097).
const SKIP_PHONE_VALIDATION = { value: () => true };

const PhoneNumberInputSection = ({ disabled, required }) => (
  <div className={element('phone-input-section')}>
    <Message
      as="label"
      className={`${element('field-label')} pb-s`}
      message={messages.phoneNumberInput}
      htmlFor="userPhoneNumber"
    />
    <PhoneNumberInput
      name="userPhoneNumber"
      id="userPhoneNumber"
      className="mv-s"
      countries={['US']}
      addInternationalOption={false}
      countryCode="US"
      disabled={disabled}
      required={required}
      validate={disabled ? SKIP_PHONE_VALIDATION : undefined}
    />
  </div>
);

PhoneNumberInputSection.propTypes = {
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

const SmsVenueDropdown = ({ tree, enabled }) => {
  const intl = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const name = `receiveNotifications.${NOTIFICATION_TYPE.CAN_RECEIVE_SMS}`;
  const allVenueIds = tree.map(({ id }) => id);
  const hasError = !!_get(errors, name, false);

  return (
    <div className="mv-s">
      <Controller
        control={control}
        name={name}
        rules={{ validate: value => !enabled || value.length > 0 || intl.formatMessage(messages.requiredTag) }}
        render={({ field: { value, onChange } }) => {
          const isAllVenues = allVenueIds.every(id => value.includes(id));
          return (
            <VenueDropdown
              options={tree}
              selectedId={isAllVenues ? ALL_VENUES_VALUE : value[0]}
              disabled={!enabled}
              hasError={hasError}
              onSelect={id => onChange(id === ALL_VENUES_VALUE ? [...allVenueIds] : [id])}
            />
          );
        }}
      />
      <ErrorMessage name={name} errors={errors} />
    </div>
  );
};

SmsVenueDropdown.propTypes = {
  tree: tagsPropTypes,
  enabled: PropTypes.bool,
};

const SmsSingleVenueSync = ({ venueId, enabled }) => {
  const { setValue } = useFormContext();
  const name = `receiveNotifications.${NOTIFICATION_TYPE.CAN_RECEIVE_SMS}`;
  const didMountRef = useRef(false);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setValue(name, enabled ? [venueId] : [], { shouldDirty: true });
  }, [enabled, venueId, name, setValue]);

  return null;
};

SmsSingleVenueSync.propTypes = {
  venueId: PropTypes.string,
  enabled: PropTypes.bool,
};

const Notifications = ({
  isLoading,
  notificationTypes,
  tagTrees,
  venueOptions,
  receiveNotifications,
  userSmsSubscriptionStatus,
  userPhoneNumber,
  handleSubmit,
}) => {
  const isMarketingOptInEnabled = useIsMarketingOptInEnabled();

  return (
    <>
      {isLoading ? (
        <LoadingDots />
      ) : (
        <Form
          id={FORM_NAME}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          defaultValues={{
            userSmsSubscriptionStatus,
            userPhoneNumber,
            receiveNotifications,
          }}>
          {({ watch, formState }) => {
            const { isDirty, isSubmitting } = formState;
            const isSmsEnabled = watch('userSmsSubscriptionStatus');
            const smsType = NOTIFICATION_TYPE.CAN_RECEIVE_SMS;
            const hasSmsSection = notificationTypes.includes(smsType);
            const emailTypes = notificationTypes.filter(type => type !== smsType);

            return (
              <>
                <Prompt when={isDirty} />
                <Message as={Title} message={messages.title} />
                {hasSmsSection && (
                  <Card className={element('card')}>
                    <Message as={CardTitle} message={notificationTypeMessages[smsType].title} />
                    <div className={element('toggle-sms')}>
                      <Switch name="userSmsSubscriptionStatus" id="userSmsSubscriptionStatus">
                        {() => (
                          <Message
                            as="label"
                            className="ml-s"
                            message={appMessages.enable}
                            htmlFor="userSmsSubscriptionStatus"
                          />
                        )}
                      </Switch>
                    </div>
                    <Message
                      as="p"
                      className={`text--dark ${element('disclaimer')}`}
                      message={notificationTypeMessages[smsType].disclaimer}
                    />
                    {tagTrees[smsType].length === 1 && (
                      <SmsSingleVenueSync venueId={tagTrees[smsType][0].id} enabled={isSmsEnabled} />
                    )}
                    {(isSmsEnabled || receiveNotifications.canReceiveSms.length > 0 || userPhoneNumber) && (
                      <>
                        <PhoneNumberInputSection disabled={!isSmsEnabled} required={isSmsEnabled} />
                        {tagTrees[smsType].length > 1 && (
                          <>
                            <Message as="p" className={element('field-label')} message={messages.smsVenueSelectLabel} />
                            <SmsVenueDropdown tree={tagTrees[smsType]} enabled={isSmsEnabled} />
                          </>
                        )}
                      </>
                    )}
                  </Card>
                )}
                {emailTypes.length > 0 && (
                  <VenueEmailsSection venueOptions={venueOptions} emailTypes={emailTypes} tagTrees={tagTrees} />
                )}
                <SaveBar visible={isDirty} submitting={isSubmitting} formName={FORM_NAME} />
              </>
            );
          }}
        </Form>
      )}
      {isMarketingOptInEnabled && <MarketingEmails />}
      <OrderNotificationsSection />
    </>
  );
};

Notifications.propTypes = {
  isLoading: PropTypes.bool,
  notificationTypes: PropTypes.arrayOf(PropTypes.oneOf(NOTIFICATION_TYPE_LIST)),
  tagTrees: PropTypes.shape(NOTIFICATION_TYPE_LIST.reduce((memo, type) => ({ ...memo, [type]: tagsPropTypes }), {})),
  venueOptions: tagsPropTypes,
  receiveNotifications: PropTypes.shape(
    NOTIFICATION_TYPE_LIST.reduce((memo, type) => ({ ...memo, [type]: PropTypes.arrayOf(PropTypes.string) }), {})
  ),
  userSmsSubscriptionStatus: PropTypes.bool,
  userPhoneNumber: PropTypes.string,
  handleSubmit: PropTypes.func,
};

export default Notifications;
